import { useState, useEffect } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import api from '../services/api'
import Reveal from '../components/landing/Reveal'
import LoadingSpinner from '../components/LoadingSpinner'
import {
  ArrowRight,
  ArrowLeft,
  Clock,
  Award,
  CheckCircle2,
  Sparkles,
  Calendar,
  BookOpen,
  Zap,
  AlertCircle,
  Users,
  CreditCard,
  Target,
} from 'lucide-react'

export default function HolidayProgramDetailPage() {
  const { courseCode } = useParams()
  const [course, setCourse] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const { data } = await api.get('/auth/holiday-offerings')
        const match = (data?.courses || []).find(
          (c) => c.course_code.toLowerCase() === String(courseCode).toLowerCase()
        )
        if (!match) {
          setNotFound(true)
        } else {
          setCourse(match)
        }
      } catch (err) {
        console.error('[HolidayProgramDetail] Failed to load:', err)
        setError(err.response?.data?.error || 'Unable to load program details.')
      } finally {
        setLoading(false)
      }
    }
    fetchCourse()
  }, [courseCode])

  if (loading) {
    return (
      <div className="min-h-screen bg-cinema-black flex justify-center items-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (notFound) {
    return <Navigate to="/holiday-programs" replace />
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cinema-black flex justify-center items-center px-4">
        <div className="max-w-lg text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-display text-white mb-2">Unable to Load Program</h2>
          <p className="text-sm text-cinema-muted mb-6">{error}</p>
          <Link to="/holiday-programs" className="cinema-btn-primary !px-6 !py-2.5 !text-xs">
            Back to Holiday Programs
          </Link>
        </div>
      </div>
    )
  }

  if (!course) return null

  const fmtUGX = (n) => 'UGX ' + (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })
  const fmtDate = (d) => {
    if (!d) return '—'
    try {
      return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    } catch {
      return String(d).slice(0, 10)
    }
  }

  const registerPath = course.is_registerable
    ? `/register?course=${encodeURIComponent(course.course_code)}&program_type=HOLIDAY&intake_id=${course.offerings[0]?.intake_id || ''}`
    : null

  // ─── Availability logic (mirrors HolidayProgramCard) ──────────────────
  const offerings = course.offerings || []
  const openOffering = offerings.find(
    (o) => o.computed_status === 'OPEN' || o.computed_status === 'CLOSING_SOON'
  )
  const upcomingOffering = !openOffering
    ? offerings.find(
        (o) =>
          o.computed_status === 'UPCOMING' ||
          Boolean(o.registration_open_date || o.start_date)
      )
    : null
  const bestOffering = openOffering || upcomingOffering || offerings[0] || null

  const hasScheduledDates = Boolean(
    bestOffering &&
      (bestOffering.registration_open_date ||
        bestOffering.registration_close_date ||
        bestOffering.start_date)
  )

  const availabilityState = openOffering
    ? 'open'
    : hasScheduledDates
      ? 'upcoming'
      : 'fallback'

  const fmtDateLong = (d) => {
    if (!d) return '—'
    try {
      return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    } catch {
      return String(d).slice(0, 10)
    }
  }

  // Parse course_knowledge JSONB fields safely
  const parseJsonField = (field) => {
    if (!field) return null
    if (Array.isArray(field)) return field
    if (typeof field === 'string') {
      try { return JSON.parse(field) } catch { return null }
    }
    return null
  }
  const learningOutcomes = parseJsonField(course.learning_outcomes)
  const practicalProjects = parseJsonField(course.practical_projects)
  const idealFor = parseJsonField(course.ideal_for)

  return (
    <div className="cinema-page min-h-screen selection:bg-cinema-glow/30 selection:text-white">
      <Helmet>
        <title>{course.title} — 6-Week Holiday Program | Synergy Multimedia Academy</title>
        <meta
          name="description"
          content={course.description
            ? `${course.description.slice(0, 155)}`
            : `${course.title} — a 6-week practical holiday program at Synergy Multimedia Academy. Build real projects and earn a certificate.`}
        />
        <link rel="canonical" href={`https://synergymultimediaacademy.com/holiday-programs/${course.course_code}`} />
        <meta property="og:title" content={`${course.title} — Holiday Program | SMA`} />
        <meta
          property="og:description"
          content={course.description || `${course.title} — 6-week practical holiday program at SMA.`}
        />
        <meta property="og:url" content={`https://synergymultimediaacademy.com/holiday-programs/${course.course_code}`} />
        <meta property="og:type" content="course" />
        {/* JSON-LD Course structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Course',
            name: course.title,
            description: course.description || `${course.title} — 6-week holiday program`,
            provider: {
              '@type': 'Organization',
              name: 'Synergy Multimedia Academy',
              url: 'https://synergymultimediaacademy.com',
            },
            educationalLevel: course.level || 'Beginner',
            courseCode: course.course_code,
            hasCourseInstance: {
              '@type': 'CourseInstance',
              courseMode: 'onsite',
              duration: 'P6W',
              inLanguage: 'en',
              location: {
                '@type': 'Place',
                name: 'Synergy Multimedia Academy Campus',
                address: {
                  '@type': 'PostalAddress',
                  addressLocality: 'Kampala',
                  addressCountry: 'UG',
                },
              },
            },
            offers: course.fee != null ? {
              '@type': 'Offer',
              price: (Number(course.fee) / 3800).toFixed(2),
              priceCurrency: 'USD',
              availability: course.is_registerable ? 'https://schema.org/InStock' : 'https://schema.org/PreOrder',
            } : undefined,
          })}
        </script>
      </Helmet>

      <div className="cinema-grain" aria-hidden="true" />

      {/* Top Navigation */}
      <nav className="cinema-glass-strong sticky top-0 z-40 border-b border-white/5">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="flex justify-between items-center h-14 sm:h-20">
            <Link
              to="/holiday-programs"
              className="flex items-center gap-2.5 sm:gap-3 group shrink-0"
            >
              <img
                src="/Logos/SMA LOGO  DARK MODE.png"
                alt="Synergy Multimedia Academy logo"
                className="h-8 sm:h-12 transition-transform duration-500 group-hover:scale-105"
              />
              <div>
                <p className="font-display text-xs sm:text-sm tracking-tight leading-tight text-white">
                  Synergy Multimedia
                </p>
                <p className="text-xs uppercase tracking-[0.25em] text-cinema-gold font-medium">
                  Academy
                </p>
              </div>
            </Link>

            <div className="flex items-center gap-2.5 sm:gap-6">
              <Link
                to="/holiday-programs"
                className="inline-flex items-center gap-1.5 sm:gap-2 text-cinema-soft hover:text-cinema-gold text-[0.7rem] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-sans font-medium transition-colors group"
              >
                <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                <span className="hidden xs:inline sm:inline">Holiday Programs</span>
              </Link>
              {registerPath && (
                <Link
                  to={registerPath}
                  className="cinema-btn-primary !px-3.5 sm:!px-5 !py-2 sm:!py-2.5 !min-h-[38px] sm:!min-h-[42px] !text-xs"
                >
                  Register Now
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="relative pt-8 pb-10 sm:pt-16 sm:pb-20 overflow-hidden bg-gradient-to-b from-cinema-charcoal/80 via-cinema-black to-cinema-black border-b border-white/5">
        <div className="cinema-glow-orb w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] -top-20 sm:-top-40 right-4 sm:right-10 opacity-30 sm:opacity-40 pointer-events-none" />

        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 relative z-10">
          <Reveal className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cinema-gold/10 border border-cinema-gold/30 text-cinema-gold text-xs uppercase tracking-[0.16em] font-sans font-semibold mb-4 sm:mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              <span>6-Week Holiday Program</span>
            </div>

            <h1 className="cinema-headline-xl text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-white font-light tracking-tight">
              {course.title}
            </h1>

            {course.description && (
              <p className="mt-3 sm:mt-6 cinema-body-lg text-xs sm:text-sm md:text-base text-cinema-soft/80 leading-relaxed max-w-2xl">
                {course.description}
              </p>
            )}

            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5 sm:gap-4">
              {availabilityState === 'open' ? (
                <Link
                  to={registerPath}
                  className="cinema-btn-primary !w-full sm:!w-auto !px-6 !py-3 !text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-2 shadow-lg shadow-cinema-gold/20 cursor-pointer min-h-[44px]"
                >
                  <span>Register Now</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              ) : availabilityState === 'upcoming' ? (
                <span className="cinema-btn-ghost !w-full sm:!w-auto !px-6 !py-3 !text-xs uppercase tracking-wider font-semibold flex items-center justify-center gap-2 min-h-[44px] text-cinema-gold border-cinema-gold/30 bg-cinema-gold/10">
                  <Calendar className="w-4 h-4" />
                  {bestOffering?.registration_open_date
                    ? `Opens ${fmtDateLong(bestOffering.registration_open_date)}`
                    : bestOffering?.start_date
                      ? `Intake: ${fmtDate(bestOffering.start_date)}`
                      : 'Upcoming Intake'}
                </span>
              ) : (
                <span className="cinema-btn-ghost !w-full sm:!w-auto !px-6 !py-3 !text-xs uppercase tracking-wider font-semibold flex items-center justify-center gap-2 min-h-[44px] opacity-70 cursor-default">
                  <Calendar className="w-4 h-4" />
                  Next intake dates to be announced
                </span>
              )}
              <Link
                to="/advisor"
                className="cinema-btn-ghost !w-full sm:!w-auto !px-5 !py-3 !text-xs uppercase tracking-wider font-semibold flex items-center justify-center gap-2 border border-white/20 hover:border-cinema-gold/60 cursor-pointer min-h-[44px]"
              >
                <span>Talk to Admissions</span>
              </Link>
            </div>
          </Reveal>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16 space-y-10 sm:space-y-16">
        {/* Program Overview */}
        <Reveal>
          <section>
            <h2 className="font-display text-xl sm:text-2xl text-white font-light mb-6">
              Program <span className="text-cinema-gold">Overview</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              <OverviewCell icon={Clock} label="Duration" value={course.duration || '6 Weeks'} />
              <OverviewCell icon={BookOpen} label="Level" value={course.level || 'Beginner Friendly'} />
              <OverviewCell icon={CreditCard} label="Fee" value={course.fee != null ? fmtUGX(course.fee) : '—'} />
              <OverviewCell icon={Sparkles} label="Program Type" value="Holiday" />
              <OverviewCell icon={Award} label="Certificate" value="Yes — Verifiable" />
              <OverviewCell icon={Users} label="Delivery" value="On Campus / Online" />
            </div>
            {course.prerequisites && (
              <p className="mt-4 text-xs text-cinema-muted font-sans">
                <span className="text-cinema-gold font-medium">Prerequisites: </span>
                {course.prerequisites || 'None — beginners welcome.'}
              </p>
            )}
          </section>
        </Reveal>

        {/* What You'll Learn — from course_knowledge.learning_outcomes */}
        {learningOutcomes && learningOutcomes.length > 0 && (
          <Reveal>
            <section>
              <h2 className="font-display text-xl sm:text-2xl text-white font-light mb-6">
                What You'll <span className="text-cinema-gold">Learn</span>
              </h2>
              <div className="space-y-3">
                {learningOutcomes.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-cinema-gold shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-cinema-soft font-sans">{item}</span>
                  </div>
                ))}
              </div>
            </section>
          </Reveal>
        )}

        {/* Practical Projects — from course_knowledge.practical_projects */}
        {practicalProjects && practicalProjects.length > 0 && (
          <Reveal>
            <section>
              <h2 className="font-display text-xl sm:text-2xl text-white font-light mb-6">
                Practical <span className="text-cinema-gold">Projects</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {practicalProjects.map((proj, i) => (
                  <div
                    key={i}
                    className="p-4 sm:p-5 bg-white/[0.02] border border-white/5 rounded-sm"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase className="w-4 h-4 text-cinema-gold" />
                      <p className="text-xs font-semibold uppercase tracking-wider text-cinema-gold font-sans">
                        Project {i + 1}
                      </p>
                    </div>
                    <p className="text-xs sm:text-sm text-cinema-soft font-sans">{proj}</p>
                  </div>
                ))}
              </div>
            </section>
          </Reveal>
        )}

        {/* Who It's For — from course_knowledge.ideal_for */}
        {idealFor && idealFor.length > 0 && (
          <Reveal>
            <section>
              <h2 className="font-display text-xl sm:text-2xl text-white font-light mb-6">
                Who It's <span className="text-cinema-gold">For</span>
              </h2>
              <div className="space-y-3">
                {idealFor.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Users className="w-4 h-4 text-cinema-gold shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-cinema-soft font-sans">{item}</span>
                  </div>
                ))}
              </div>
            </section>
          </Reveal>
        )}

        {/* Curriculum Structure (public outline: modules + lesson titles + assignment titles) */}
        {course.curriculum && course.curriculum.length > 0 && (
          <Reveal>
            <section>
              <div className="flex items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="font-display text-xl sm:text-2xl text-white font-light">
                    Course <span className="text-cinema-gold">Curriculum</span>
                  </h2>
                  <p className="text-xs text-cinema-muted mt-1 font-sans">
                    6-week practical syllabus · 18 guided lessons · 6 capstone projects
                  </p>
                </div>
                <div className="hidden sm:flex items-center gap-2 text-xs text-cinema-gold font-mono">
                  <BookOpen className="w-3.5 h-3.5" />
                  <span>{course.curriculum.length} Modules</span>
                </div>
              </div>

              <div className="space-y-3">
                {course.curriculum.map((mod) => (
                  <div
                    key={mod.module_id}
                    className="p-4 sm:p-5 bg-white/[0.02] border border-white/5 hover:border-cinema-gold/20 transition-all rounded-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[0.65rem] font-mono text-cinema-gold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cinema-gold/10 border border-cinema-gold/20">
                            Week {mod.week_number || mod.module_number}
                          </span>
                          <h3 className="font-display text-sm sm:text-base text-white font-medium">
                            {mod.title}
                          </h3>
                        </div>
                        {mod.description && (
                          <p className="text-xs text-cinema-soft/70 font-sans mt-2 leading-relaxed">
                            {mod.description}
                          </p>
                        )}

                        {/* Lesson outline */}
                        {mod.lessons && mod.lessons.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-white/5 space-y-1.5">
                            {mod.lessons.map((lesson) => (
                              <div
                                key={lesson.lesson_id}
                                className="flex items-center justify-between text-xs text-cinema-muted font-sans"
                              >
                                <span className="flex items-center gap-2 text-cinema-soft/80">
                                  <span className="w-1 h-1 rounded-full bg-cinema-gold/60" />
                                  Lesson {lesson.lesson_number}: {lesson.title}
                                </span>
                                {lesson.duration_minutes && (
                                  <span className="text-[0.7rem] text-cinema-muted/60 font-mono">
                                    {lesson.duration_minutes}m
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </Reveal>
        )}

        {/* Available Holiday Intakes */}
        <Reveal>
          <section>
            <h2 className="font-display text-xl sm:text-2xl text-white font-light mb-6">
              Available <span className="text-cinema-gold">Holiday Intakes</span>
            </h2>

            {course.offerings && course.offerings.length > 0 ? (
              <div className="space-y-4">
                {course.offerings.map((offering) => {
                  const isOfferingOpen =
                    offering.computed_status === 'OPEN' ||
                    offering.computed_status === 'CLOSING_SOON'
                  const offeringRegisterPath = `/register?course=${encodeURIComponent(course.course_code)}&program_type=HOLIDAY&intake_id=${offering.intake_id}`
                  // Find matching sessions for this course
                  const courseSessions = course.sessions || []
                  return (
                    <div
                      key={offering.link_id || offering.intake_id}
                      className="p-5 sm:p-6 bg-white/[0.02] border border-cinema-gold/20 rounded-sm"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <p className="font-display text-lg text-cinema-gold font-semibold">
                              {offering.intake_name}
                            </p>
                            {isOfferingOpen ? (
                              <span className="inline-flex items-center gap-1 text-[0.65rem] text-green-400 font-sans font-medium uppercase tracking-wider px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/20">
                                <CheckCircle2 className="w-3 h-3" /> Registration Open
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[0.65rem] text-cinema-gold font-sans font-medium uppercase tracking-wider px-2 py-0.5 rounded-full bg-cinema-gold/10 border border-cinema-gold/20">
                                <Calendar className="w-3 h-3" /> Scheduled
                              </span>
                            )}
                          </div>
                          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs text-cinema-soft font-sans">
                            {offering.start_date && (
                              <div className="flex items-center gap-2">
                                <Calendar className="w-3.5 h-3.5 text-cinema-gold/60" />
                                <span>Program Dates: {fmtDate(offering.start_date)} – {fmtDate(offering.end_date)}</span>
                              </div>
                            )}
                            {offering.registration_open_date && (
                              <div className="flex items-center gap-2">
                                <Clock className="w-3.5 h-3.5 text-cinema-gold/60" />
                                <span>
                                  {isOfferingOpen
                                    ? `Registration closes ${fmtDate(offering.registration_close_date)}`
                                    : `Registration: ${fmtDate(offering.registration_open_date)} – ${fmtDate(offering.registration_close_date)}`}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Session schedule info */}
                          {courseSessions.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-white/5 space-y-2">
                              <p className="text-[0.65rem] uppercase tracking-wider text-cinema-muted font-sans font-semibold">
                                Schedule
                              </p>
                              {courseSessions.map((s, i) => (
                                <div key={i} className="text-xs text-cinema-soft font-sans flex flex-wrap gap-x-4 gap-y-1">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3 text-cinema-gold/60" />
                                    {s.time_display || s.time_slot}
                                  </span>
                                  {s.days_of_week && (
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3 text-cinema-gold/60" />
                                      {s.days_of_week}
                                    </span>
                                  )}
                                  {s.available_seats != null && (
                                    <span className="flex items-center gap-1">
                                      <Users className="w-3 h-3 text-cinema-gold/60" />
                                      {s.available_seats} seats
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {isOfferingOpen ? (
                          <Link
                            to={offeringRegisterPath}
                            className="cinema-btn-primary !px-5 !py-2.5 !text-xs shrink-0 flex items-center gap-1.5 self-start"
                          >
                            Register
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        ) : (
                          <span className="text-xs text-cinema-gold font-sans font-medium px-4 py-2 bg-cinema-gold/10 border border-cinema-gold/20 rounded-sm shrink-0 self-start">
                            Opens {fmtDate(offering.registration_open_date || offering.start_date)}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="p-6 bg-white/[0.02] border border-white/5 rounded-sm text-center">
                <Calendar className="w-8 h-8 text-cinema-muted mx-auto mb-3" />
                {availabilityState === 'upcoming' && bestOffering?.registration_open_date ? (
                  <>
                    <p className="text-sm text-cinema-soft font-sans">
                      Registration opens {fmtDateLong(bestOffering.registration_open_date)}.
                    </p>
                    <p className="text-xs text-cinema-muted mt-1 font-sans">
                      Check back on that date to register, or talk to admissions to be notified.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-cinema-soft font-sans">
                      Next intake dates to be announced.
                    </p>
                    <p className="text-xs text-cinema-muted mt-1 font-sans">
                      Check back soon or talk to admissions to be notified when registration opens.
                    </p>
                  </>
                )}
                <Link to="/advisor" className="mt-4 inline-flex items-center gap-1.5 text-xs text-cinema-gold hover:text-white transition-colors">
                  <Sparkles size={14} /> Talk to Admissions
                </Link>
              </div>
            )}
          </section>
        </Reveal>

        {/* Bottom CTA */}
        <Reveal>
          <div className="p-6 sm:p-8 bg-gradient-to-r from-cinema-gold/10 to-transparent border border-cinema-gold/20 rounded-sm text-center">
            <h3 className="font-display text-xl sm:text-2xl text-white font-light mb-3">
              Ready to <span className="text-cinema-gold">make your holiday count?</span>
            </h3>
            <p className="text-sm text-cinema-muted font-sans mb-6 max-w-md mx-auto">
              Limited seats available. Apply now to secure your place.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {registerPath ? (
                <Link to={registerPath} className="cinema-btn-primary !px-6 !py-3 !text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-2">
                  Register Now <ArrowRight className="w-4 h-4" />
                </Link>
              ) : (
                <Link to="/advisor" className="cinema-btn-primary !px-6 !py-3 !text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-2">
                  Talk to Admissions <ArrowRight className="w-4 h-4" />
                </Link>
              )}
              <Link to="/holiday-programs" className="cinema-btn-ghost !px-6 !py-3 !text-xs uppercase tracking-wider font-semibold flex items-center justify-center gap-2">
                <ArrowLeft className="w-4 h-4" /> All Holiday Programs
              </Link>
            </div>
          </div>
        </Reveal>
      </main>
    </div>
  )
}

function OverviewCell({ icon: Icon, label, value }) {
  return (
    <div className="p-3 sm:p-4 rounded-sm bg-white/[0.02] border border-white/5">
      <div className="flex items-center gap-1.5 sm:gap-2 text-cinema-gold text-xs uppercase tracking-wider font-sans mb-1 font-semibold">
        <Icon className="w-4 h-4" />
        <span>{label}</span>
      </div>
      <p className="font-display text-sm sm:text-base text-white font-light">{value}</p>
    </div>
  )
}
